import React, { useState, useEffect, useContext } from "react";
import { useFirestore } from "../firebase/FirestoreContext";
import { collection, getDocs, addDoc, updateDoc, doc, deleteDoc } from "firebase/firestore";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { EnvContext } from "../../context/EnvContext";
import config from "../../config";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage"; // Firebase Storage imports
// image MUI icon
import { ImageOutlined } from "@mui/icons-material";

const ProductTags = () => {
  const environment = useContext(EnvContext);
  const db = useFirestore();
  const storage = getStorage();

  const collectionPath = environment === "staging" ? config.enveironment.staging.collectionPath : config.enveironment.production.collectionPath;

  const [tags, setTags] = useState([]);
  const [newTagName, setNewTagName] = useState("");
  const [editMode, setEditMode] = useState(null);
  const [editedTagName, setEditedTagName] = useState("");
  const [tagInput, setTagInput] = useState({});
  const [tagEditInput, setTagEditInput] = useState("");
  const [tagEditMode, setTagEditMode] = useState(null);
  const [imageInput, setImageInput] = useState({}); // State for handling image input

  useEffect(() => {
    fetchTags();
  }, [db, collectionPath]);

  const fetchTags = async () => {
    const tagsCollectionRef = collection(db, `${collectionPath}SooperstockSettings/ProductTags/DataBase`);
    const querySnapshot = await getDocs(tagsCollectionRef);
    const tagsArray = querySnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));
    setTags(tagsArray);
  };

  const handleTagInputChange = (tagId, value) => {
    setTagInput((prev) => ({ ...prev, [tagId]: value }));
  };

  const handleImageInputChange = (tagId, file) => {
    setImageInput((prev) => ({ ...prev, [tagId]: file }));
  };

  const handleAddTag = async () => {
    if (!newTagName.trim()) {
      toast.error("Tag name cannot be empty");
      return;
    }

    try {
      const newTag = { name: newTagName, tags: [], image_url: "" }; // Adding image_url to new tag data
      const docRef = await addDoc(collection(db, `${collectionPath}SooperstockSettings/ProductTags/DataBase`), newTag);
      toast.success("Tag added successfully");
      setNewTagName("");
      fetchTags();
    } catch (error) {
      toast.error("Error adding tag");
    }
  };

  const handleEditTag = async (tagId) => {
    if (!editedTagName.trim()) {
      toast.error("Tag name cannot be empty");
      return;
    }

    try {
      const tagRef = doc(db, `${collectionPath}SooperstockSettings/ProductTags/DataBase`, tagId);
      await updateDoc(tagRef, { name: editedTagName });
      toast.success("Tag updated successfully");
      setEditMode(null);
      fetchTags();
    } catch (error) {
      toast.error("Error updating tag");
    }
  };

  const handleDeleteTag = async (tagId) => {
    if (!window.confirm("Are you sure you want to delete this tag?")) return;

    try {
      const tagRef = doc(db, `${collectionPath}SooperstockSettings/ProductTags/DataBase`, tagId);
      await deleteDoc(tagRef);
      toast.success("Tag deleted successfully");
      fetchTags();
    } catch (error) {
      toast.error("Error deleting tag");
    }
  };

  const handleAddTagToTag = async (tagId) => {
    if (!tagInput[tagId] || !tagInput[tagId].trim()) {
      toast.error("Tag cannot be empty");
      return;
    }

    try {
      const tagRef = doc(db, `${collectionPath}SooperstockSettings/ProductTags/DataBase`, tagId);
      const updatedTag = tags.find((tag) => tag.id === tagId);
      const updatedTags = [...(updatedTag.tags || []), tagInput[tagId]];

      await updateDoc(tagRef, { tags: updatedTags });
      toast.success("Tag added successfully");
      setTagInput((prev) => ({ ...prev, [tagId]: "" }));
      fetchTags();
    } catch (error) {
      toast.error("Error adding tag");
    }
  };

  const handleDeleteTagFromTag = async (tagId, tagToDelete) => {
    try {
      const tagRef = doc(db, `${collectionPath}SooperstockSettings/ProductTags/DataBase`, tagId);
      const updatedTag = tags.find((tag) => tag.id === tagId);
      const updatedTags = updatedTag.tags.filter((tag) => tag !== tagToDelete);

      await updateDoc(tagRef, { tags: updatedTags });
      toast.success("Tag deleted successfully");
      fetchTags();
    } catch (error) {
      toast.error("Error deleting tag");
    }
  };

  const handleUploadImage = async (tagId) => {
    const file = imageInput[tagId];
    if (!file) return;

    try {
      const storageRef = ref(storage, `Tags/${tagId}`);
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);

      // Update Firestore with the image URL
      const tagRef = doc(db, `${collectionPath}SooperstockSettings/ProductTags/DataBase`, tagId);
      await updateDoc(tagRef, { image_url: url });

      // Update local state with new image URL
      setTags((prev) => prev.map((tag) => (tag.id === tagId ? { ...tag, image_url: url } : tag)));
      toast.success("Image uploaded successfully");
    } catch (error) {
      toast.error("Error uploading image");
    }
  };

  return (
    <div>
      <ToastContainer position="top-center" autoClose={5000} hideProgressBar={false} />
      <h2>Product Tags</h2>
      <table style={styles.table}>
        <thead>
          <tr style={{ backgroundColor: "rgba(49,57,66,.1)" }}>
            <th style={styles.th} colSpan={1}>
              Tags
            </th>
          </tr>
        </thead>
        <tbody>
          {tags.map((tag, index) => (
            <div
              key={tag.id}
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "10px",
                backgroundColor: index % 2 === 0 ? "#fafafa" : "",
                borderBottom: "1px solid #ccc",
              }}
            >
              {/* Tag Name and Edit/Delete Buttons */}
              <div style={{ display: "flex", alignItems: "center", flex: 1 }}>
                {editMode === tag.id ? (
                  <input
                    type="text"
                    value={editedTagName}
                    onChange={(e) => setEditedTagName(e.target.value)}
                    style={{ ...styles.input, marginRight: "10px", flex: 1 }}
                  />
                ) : (
                  <span style={{ marginRight: "10px", flex: 1 }}>{tag.name}</span>
                )}
                {/* Image Input and Preview */}
                <div style={{ display: "flex", alignItems: "center" }}>
                  {/* Image Preview */}
                  {tag.image_url ? (
                    <img
                      src={tag.image_url}
                      alt={tag.name}
                      style={{
                        width: "50px",
                        height: "50px",
                        objectFit: "cover",
                        borderRadius: "5px",
                        marginLeft: "10px",
                      }}
                    />
                  ) : (
                    <div
                      style={{
                        width: "50px",
                        height: "50px",
                        borderRadius: "5px",
                        marginLeft: "10px",
                        backgroundColor: "#ccc",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        color: "gray",
                      }}
                    >
                      <ImageOutlined />
                    </div>
                  )}

                  <input
                    type="file"
                    onChange={(e) => handleImageInputChange(tag.id, e.target.files[0])}
                    accept="image/*"
                    style={{ display: "none" }} // Hiding the default file input
                    id={`image-upload-${tag.id}`}
                  />
                  <label
                    htmlFor={`image-upload-${tag.id}`}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      cursor: "pointer",
                      padding: "8px 10px",
                      backgroundColor: "lightblue",
                      color: "black",
                      borderRadius: "4px",
                      marginLeft: "10px",
                      marginRight: "10px",
                      fontSize: ".7rem",
                    }}
                  >
                    Image
                  </label>
                </div>
                <div style={{ display: "flex", gap: "5px" }}>
                  {editMode === tag.id ? (
                    <>
                      <button onClick={() => handleEditTag(tag.id)} style={styles.button}>
                        Save
                      </button>
                      <button onClick={() => setEditMode(null)} style={{ ...styles.button, backgroundColor: "silver", color: "black" }}>
                        Cancel
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={() => {
                        setEditMode(tag.id);
                        setEditedTagName(tag.name);
                      }}
                      style={styles.button}
                    >
                      Edit
                    </button>
                  )}
                  <button onClick={() => handleDeleteTag(tag.id)} style={{ ...styles.button, backgroundColor: "red" }}>
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </tbody>
      </table>
      {/** Add new tag */}
      <div style={{ display: "flex", alignItems: "center", marginTop: 20 }}>
        <input type="text" value={newTagName} onChange={(e) => setNewTagName(e.target.value)} placeholder="New Tag Name" style={styles.input} />
        <button onClick={handleAddTag} style={styles.button}>
          Add
        </button>
      </div>
    </div>
  );
};

const styles = {
  table: { borderCollapse: "collapse", width: "100%" },
  th: { padding: "8px", textAlign: "left", borderBottom: "1px solid #ccc", fontSize: 12 },
  td: { padding: "8px" },
  input: { padding: "10px", width: "100%", border: "1px solid #ccc", borderRadius: "4px" },
  button: { padding: "8px 12px", backgroundColor: "#478169", color: "white", border: "none", borderRadius: "4px", cursor: "pointer", marginLeft: "5px" },
  tag: { marginRight: "5px", padding: "6px 12px", backgroundColor: "#ccc", borderRadius: "25px", display: "inline-block", fontSize: 12 },
  deleteTagButton: { marginLeft: "5px", cursor: "pointer", border: "none", background: "none" },
  addTagButton: { marginLeft: "5px", cursor: "pointer", padding: "5px", backgroundColor: "#478169", color: "white", border: "none", borderRadius: "4px" },
};

export default ProductTags;
